import { ACCESS_TOKEN_COOKIE_NAME, COOKIE_DOMAIN, REFRESH_TOKEN_EXISTS_COOKIE_NAME } from '../constants/Constants';
import { isLocalhost } from '../helpers/Helpers';
import { LogDebug, LogMsg } from '../helpers/Logging';
import { getPublicServiceExternalUrlByK8sInternalUrl } from '../helpers/PoddedUrlTransformer';
import { validateServiceAccess } from '../helpers/AccessValidator';
import { IEmServiceDetails, ILoggerDetails } from '../types/types';

interface IAuthVerificationHapiMiddlewareParams {
  hapiServer: any;
  emService: IEmServiceDetails;
  emReAuthenticationServiceBaseUrl: string;
  logger?: ILoggerDetails;
}

/**
 * This middleware function will use the Hapi instance provided by the consuming service to handle authentication in an Hapi application on PAGE requests only.
 *
 * ⚠️⚠️⚠️ !IMPORTANT 🚩🚩🚩: THIS MIDDLEWARE RELIES ON SPECIFIC "k8s internal pod URL" STRUCTURE. IF THAT IS CHANGED, MIDDLEWARE WOULD HAVE TO BE UPDATED ACCORDINGLY, OTHERWISE IT MAY BREAK.
 *
 * @param hapiServer - The Hapi application instance. This middleware function will use the hapi instance provided by the consuming service
 * @param emService - Details about the service using the middleware.
 * @param emReAuthenticationServiceBaseUrl - The URL to redirect to for reauthentication.
 * @param logger - [OPTIONAL] The logger instance for logging information.
 */
export const authVerificationHapiMiddleware = ({
  hapiServer,
  emService,
  emReAuthenticationServiceBaseUrl,
  logger,
}: IAuthVerificationHapiMiddlewareParams) => {
  //
  LogDebug(
    logger,
    `authVerificationHapiMiddleware called for ${JSON.stringify(
      emService
    )} service; reauth url: ${emReAuthenticationServiceBaseUrl}`
  );

  if (!hapiServer) {
    LogMsg(logger, `authVerificationHapiMiddleware :: hapi server instance not provided`, 'error');
  }

  if (!emReAuthenticationServiceBaseUrl) {
    LogMsg(logger, `'emReAuthenticationServiceBaseUrl' is required in authVerificationHapiMiddleware`, 'error');
  }

  hapiServer.ext('onPreResponse', async (request: any, h: any) => {
    const isBot = request.headers['bot'] === 'true';

    if (isBot) {
      return h.continue;
    }

    const accept = request.headers['accept'] || '';
    // this logic only applies to html page requests (and should ignore css, js, images etc.)
    if (!accept.includes('text/html')) {
      return h.continue;
    }

    LogDebug(logger, `authVerificationHapiMiddleware :: all cookies ${JSON.stringify(request.state)}`);

    // get auth cookies
    const emAuthCookies = {
      em_rte: request.state[REFRESH_TOKEN_EXISTS_COOKIE_NAME],
      em_at: request.state[ACCESS_TOKEN_COOKIE_NAME],
    };

    LogDebug(logger, `authVerificationHapiMiddleware :: emAuthCookies ${JSON.stringify(emAuthCookies)}`);

    try {
      // get the full external URL (from the k8s internal pod URL)
      // NOTE: this relies on how the INFRA is set up for this service (this middleware is specific to SEARCH service)
      const podUrl = `${request.server.info.protocol}://${request.info.host}${request.path}${request.url.search}`;
      const fullExternalUrl = getPublicServiceExternalUrlByK8sInternalUrl(podUrl);

      LogDebug(
        logger,
        `authVerificationHapiMiddleware ::     podUrl: ${podUrl}     fullExternalUrl: ${fullExternalUrl}`
      );

      // validate the service access
      const { redirectUrl, emCookiesToTrash } = await validateServiceAccess({
        originalUrl: fullExternalUrl,
        emService,
        emAuthCookies,
        emReAuthenticationServiceBaseUrl,
        logger,
      });

      // clear cookies if needed
      if (emCookiesToTrash?.length) {
        const cookieDomain = isLocalhost(fullExternalUrl) ? new URL(fullExternalUrl).hostname : COOKIE_DOMAIN;
        //
        emCookiesToTrash.forEach((cookie) => {
          if (request.state[cookie]) {
            LogDebug(logger, `authVerificationHapiMiddleware :: clearing cookie ${cookie} on domain ${cookieDomain}`);

            h.unstate(cookie, { domain: cookieDomain, path: '/' }); // won't work without domain & path
          }
        });
      }

      // redirect if needed
      if (redirectUrl) {
        LogDebug(logger, `authVerificationHapiMiddleware :: redirecting`);
        return h
          .redirect(redirectUrl)
          .code(307) // NOTE: (while 301 can throw off Akamai and Bots), the 307 (will not signal a "permanent" redirect). It is used to preserve the request method (GET, POST, etc.) and the request body. While 302 may change the method to GET, using 307 prevents accidental method changes and data loss during redirects.
          .takeover();
      }
      return h.continue;
    } catch (error) {
      LogMsg(
        logger ? logger : { loggerInstance: console, logLevel: 'info' },
        `authVerificationHapiMiddleware :: error during validation: ${error}`,
        'error'
      );
      return h.continue; // Continue on error rather than failing the request
    }
  });

  LogDebug(logger, 'authVerificationHapiMiddleware :: done!');
};
